import Component from '@glimmer/component'
import { colors } from '@colors'
import { t, type IntlService } from 'ember-intl'
import { service } from '@ember/service'
import type { SeriesBarOptions } from 'highcharts'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'

export interface StadiumEventsData {
  spelling: number
  grammar: number
  vocabulary: number
  usage: number
}

interface Args {
  data: StadiumEventsData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardStadiumEvents extends Component<Signature> {
  @service intl!: IntlService

  readonly eventTypes: string[] = ['spelling', 'grammar', 'vocabulary', 'usage'] as const

  get categories(): string[] {
    return this.eventTypes.map((eventType) => {
      switch (eventType) {
        case 'spelling':
          return this.intl.t(`reporting.charts.stadiumEvents.categories.spelling`)
        case 'grammar':
          return this.intl.t(`reporting.charts.stadiumEvents.categories.grammar`)
        case 'vocabulary':
          return this.intl.t(`reporting.charts.stadiumEvents.categories.vocabulary`)
        case 'usage':
          return this.intl.t(`reporting.charts.stadiumEvents.categories.usage`)
        default:
          return ''
      }
    })
  }

  get colors(): string[] {
    const spelling = colors.oceanyBlue300
    const grammar = colors.oceanyBlue400
    const vocabulary = colors.juicyOrange300
    const usage = colors.purplyPink300

    return [spelling, grammar, vocabulary, usage]
  }

  get chartRows(): SeriesBarOptions[] {
    const { spelling, grammar, vocabulary, usage } = this.args.data
    const data = [spelling, grammar, vocabulary, usage]

    return [{ data, type: 'bar' as const }]
  }

  <template>
    <div data-test-giraffe-dashboard-stadium-events class="flex h-full flex-col" ...attributes>
      <div class="flex justify-between">
        <p class="text-dusty-black-500 mb-3 pr-15 pl-0 font-[12px]">
          {{t "reporting.charts.dashboard.stadiumEvents.overallEventsCompleted"}}
        </p>
      </div>
      <div class="relative grow">
        <div class="absolute inset-0">
          <SeriesBar
            @data={{this.chartRows}}
            @categories={{this.categories}}
            @colors={{this.colors}}
            @colorByPoint={{true}}
            @groupPadding={{0.15}}
            @yAxisLabel={{t "reporting.charts.dashboard.stadiumEvents.numberOfPlays"}}
          />
        </div>
      </div>
    </div>
  </template>
}

export default DashboardStadiumEvents
